import { Button } from "@/components/ui/button";
import { useGetAllGroups } from "@/hooks/queries/groups";
import {
  // MessageSquare,
  ChevronRight,
  HomeIcon,
  Plus,
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useUser } from "@/hooks/queries/auth";
import { memo, useCallback, useEffect, useMemo, startTransition, useState } from "react";
// import {
//   Tooltip,
//   TooltipContent,
//   TooltipTrigger,
// } from "@/components/ui/tooltip";
import { CreateNewGroupDialog } from "@/components/groups/CreateNewGroupDialog";
import GroupSidebarContextMenu from "@/components/groups/GroupSidebarContextMenu";
import GroupSidebarItemName from "@/components/groups/GroupSidebarItemName";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import type { GroupDetails } from "@/types/groups";
import { UnreadCount } from "@/components/wrappers/UnreadWrapper";
import {
  useUnreadGroupChatCountsByGroupId,
  useUnreadNotifications,
} from "@/hooks/queries/notification";
import useItemStore from "@/stores/useItem";
import useChatStore from "@/stores/useChatStore";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import { useSortGroupsMutation } from "@/hooks/queries/groups";
import { getQueryClient } from "@/integrations/tanstack-query/RootProvider";
import { useIsMobile } from '@/hooks/useMobile';
import { useSidebar } from '@ui/sidebar';
type GroupInsertPosition = "above" | "below";

// Keeps the dragged row's transform on the y-axis only, so it can't drift
// horizontally and expand the list's scrollable width.
const restrictToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0,
});

type GroupsProps = {
  /** When true, renders only the list for the main content column (no drawer scroll shell). */
  embeddedInMainContent?: boolean;
};

/**
 * Groups list — used in the groups drawer and on the main `/groups` page.
 */
function Groups({ embeddedInMainContent = false }: GroupsProps) {
  const isMobileViewOnly = useIsMobile();
  const { data: groups, isPending: isGroupsLoading } = useGetAllGroups();
  const { data: unreadGroupChatCountsByGroupId } = useUnreadGroupChatCountsByGroupId();
  const { refetch: refetchUnreadNotifications } = useUnreadNotifications();
  const { mutateAsync: sortGroups } = useSortGroupsMutation();

  useEffect(() => {
    void refetchUnreadNotifications();
  }, [refetchUnreadNotifications]);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );
  const { queryClient } = getQueryClient();

  // Separate groups into private and non-private
  const { privateGroups, nonPrivateGroups } = useMemo(() => {
    if (!groups?.data) {
      return { privateGroups: [], nonPrivateGroups: [] };
    }

    const privateGroupsList = groups.data.filter(
      (group) => group.isPrivate === true,
    );
    const nonPrivateGroupsList = groups.data.filter(
      (group) => group.isPrivate !== true,
    );

    return {
      privateGroups: privateGroupsList,
      nonPrivateGroups: nonPrivateGroupsList,
    };
  }, [groups?.data]);

  /**
   * Handle drag end event for reordering non-private groups
   * Updates all group indices via API using bulk sort endpoint
   */
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (isMobileViewOnly) return;
      // event.activatorEvent.preventDefault();
      const { active, over } = event;
      if (
        !over ||
        active.id === over.id ||
        !nonPrivateGroups.length ||
        !groups?.data
      )
        return;

      const activeId = String(active.id);
      const overId = String(over.id);

      // Find old and new indices within non-private groups
      const oldIndex = nonPrivateGroups.findIndex(
        (group) => String(group.id) === activeId,
      );
      const newIndex = nonPrivateGroups.findIndex(
        (group) => String(group.id) === overId,
      );

      if (oldIndex === -1 || newIndex === -1) return;

      // Store original groups for rollback
      const originalGroups = [...groups.data];

      // Reorder non-private groups
      const reorderedNonPrivateGroups = arrayMove(
        nonPrivateGroups,
        oldIndex,
        newIndex,
      );

      // Get max index from private groups to continue indexing for non-private groups
      const maxPrivateIndex =
        privateGroups.length > 0
          ? Math.max(...privateGroups.map((g) => g.index || 0))
          : -1;

      // Update indices for non-private groups (starting after the highest private group index)
      const updatedNonPrivateGroups = reorderedNonPrivateGroups.map(
        (group, index) => ({
          ...group,
          index: maxPrivateIndex + index + 1,
        }),
      );

      // Combine private (with original indices) and updated non-private groups
      const allUpdatedGroups = [...privateGroups, ...updatedNonPrivateGroups];

      // Update query cache optimistically
      queryClient.setQueryData(
        ["groups"],
        (old: { data: GroupDetails[] } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            data: allUpdatedGroups,
          };
        },
      );

      // Prepare sort data for API - all groups with their new indices
      const sortGroupData = allUpdatedGroups.map((group) => ({
        groupId: String(group.id),
        index: String(group.index),
      }));

      // Make API call to sort all groups
      startTransition(() => {
        sortGroups(sortGroupData)
          .then(() => {
            // Success - cache already updated
          })
          .catch((error: Error) => {
            // Revert on error
            queryClient.setQueryData(
              ["groups"],
              (old: { data: GroupDetails[] } | undefined) => {
                if (!old) return old;
                return {
                  ...old,
                  data: originalGroups,
                };
              },
            );
            toast.error(error.message || "Failed to update group order");
          });
      });
    },
    [isMobileViewOnly, nonPrivateGroups, privateGroups, groups?.data, queryClient, sortGroups],
  );

  /**
   * Inserts a newly created group relative to an anchor group and persists order via sort API.
   */
  const handleInsertGroup = useCallback(
    async (
      createdGroup: GroupDetails,
      anchorGroupId: string,
      position: GroupInsertPosition,
    ) => {
      const cachedGroups = queryClient.getQueryData(["groups"]) as
        | { data: GroupDetails[] }
        | undefined;
      const sourceGroups = cachedGroups?.data?.length
        ? [...cachedGroups.data]
        : [...(groups?.data || [])];
      const createdGroupId = String(createdGroup.id);

      if (!sourceGroups.some((group) => String(group.id) === createdGroupId)) {
        sourceGroups.push(createdGroup);
      }

      const groupToInsert =
        sourceGroups.find((group) => String(group.id) === createdGroupId) ||
        createdGroup;
      const groupsWithoutCreated = sourceGroups.filter(
        (group) => String(group.id) !== createdGroupId,
      );
      const anchorIndex = groupsWithoutCreated.findIndex(
        (group) => String(group.id) === String(anchorGroupId),
      );

      if (anchorIndex === -1) return;

      const insertionIndex =
        position === "above" ? anchorIndex : anchorIndex + 1;
      const reorderedGroups = [...groupsWithoutCreated];
      reorderedGroups.splice(insertionIndex, 0, groupToInsert);

      const groupsWithUpdatedIndex = reorderedGroups.map((group, index) => ({
        ...group,
        index,
      }));
      const originalGroups = [...sourceGroups];

      queryClient.setQueryData(
        ["groups"],
        (old: { data: GroupDetails[] } | undefined) => {
          if (!old) {
            return {
              status: true,
              data: groupsWithUpdatedIndex,
            };
          }
          return {
            ...old,
            data: groupsWithUpdatedIndex,
          };
        },
      );

      try {
        await sortGroups(
          groupsWithUpdatedIndex.map((group) => ({
            groupId: String(group.id),
            index: String(group.index),
          })),
        );
      } catch (error) {
        queryClient.setQueryData(
          ["groups"],
          (old: { data: GroupDetails[] } | undefined) => {
            if (!old) {
              return {
                status: true,
                data: originalGroups,
              };
            }
            return {
              ...old,
              data: originalGroups,
            };
          },
        );
        toast.error((error as Error)?.message || "Failed to insert group");
      }
    },
    [groups?.data, queryClient, sortGroups],
  );

  const groupsListContent = (
    <div className={cn("mx-auto max-w-4xl space-y-4", embeddedInMainContent ? "px-2 pt-2" : "p-2")}>
      {isGroupsLoading ? (
        <GroupsLoadingSkeleton />
      ) : !groups?.data || groups.data.length === 0 ? (
        <div className="flex h-full w-full items-center justify-center py-12">
          No groups found
        </div>
      ) : (
        <>
          {privateGroups.length > 0 &&
            privateGroups.map((group) => (
              <GroupItemView
                key={group.id}
                group={group}
                unreadChatCount={
                  unreadGroupChatCountsByGroupId?.[String(group.id)] ?? 0
                }
                onInsertGroup={handleInsertGroup}
              />
            ))}

          {privateGroups.length > 0 && nonPrivateGroups.length > 0 && (
            <div className="border-border my-4 border-t" />
          )}

          {nonPrivateGroups.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext
                items={nonPrivateGroups.map((group) => group.id)}
                strategy={verticalListSortingStrategy}
              >
                {nonPrivateGroups.map((group) => (
                  <SortableGroupItemView
                    key={group.id}
                    group={group}
                    unreadChatCount={
                      unreadGroupChatCountsByGroupId?.[String(group.id)] ?? 0
                    }
                    onInsertGroup={handleInsertGroup}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </>
      )}

      {embeddedInMainContent && (
        <div className="flex items-center justify-center py-4">
          <CreateNewGroupDialog />
        </div>
      )}
    </div>
  );

  if (embeddedInMainContent) {
    return groupsListContent;
  }

  return (
    <div className="flex h-full w-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">{groupsListContent}</div>
    </div>
  );
}

export default Groups;

/**
 * Loading skeleton component for groups list
 * Matches the structure of GroupItemView for consistent UI
 */
function GroupsLoadingSkeleton() {
  const skeletonCount = 6;

  return (
    <div className="space-y-2">
      {Array.from({ length: skeletonCount }).map((_, index) => (
        <div
          key={index}
          className="border-border my-2 flex h-14 grow items-center justify-between rounded-md border px-4 bg-[var(--color-surface-light-sl-1)]"
        >
          {/* Left side: Icon and name */}
          <div className="flex w-full items-center gap-3">
            {/* Users icon skeleton */}
            <Skeleton className="h-5 w-5 rounded" />
            {/* Group name skeleton */}
            <Skeleton className="h-5 w-32" />
          </div>
          {/* Right side: Unread count, share button, chevron */}
          <div className="flex items-center gap-3">
            {/* Optional unread count skeleton (randomly show some) */}
            {index % 3 === 0 && <Skeleton className="h-6 w-6 rounded-full" />}
            {/* Share button skeleton */}
            <Skeleton className="h-8 w-8 rounded" />
            {/* Chevron icon skeleton */}
            <Skeleton className="h-5 w-5 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

interface GroupViewProps {
  group: GroupDetails;
  unreadChatCount?: number;
  onInsertGroup?: (
    createdGroup: GroupDetails,
    anchorGroupId: string,
    position: GroupInsertPosition,
  ) => Promise<void> | void;
}

Groups.PageHeader = memo(() => {
  const { user } = useUser();
  const organizationName = useMemo(() => {
    if (!user?.organization) return undefined;
    return user.organization.name;
  }, [user]);

  return (
    <>
      <div className="flex items-center justify-between border-b bg-[#F7F6F3] px-4 py-2 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <HomeIcon />
            {organizationName ? `Groups for ${organizationName}` : "Home"}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <CreateNewGroupDialog />
        </div>
      </div>
    </>
  );
});

/**
 * Actions compound component for Groups drawer header
 *
 * Contains the Create New Group button to be used in the DrawerHeader actions prop
 *
 * @returns Group of action buttons for the drawer header
 */
Groups.Actions = memo(({ compact = false }: { compact?: boolean }) => {
  return (
    <div className="flex items-center gap-2">
      <CreateNewGroupDialog compact={compact} />
    </div>
  );
});

// Groups.ShareGroup = memo(
//   ({ onOpenRoot, unreadChatCount = 0 }: { onOpenRoot: () => void; unreadChatCount?: number }) => {
//     const handleOpenRoot = useCallback(
//       (e: React.MouseEvent) => {
//         e.preventDefault();
//         e.stopPropagation();
//         onOpenRoot();
//       },
//       [onOpenRoot],
//     );

//     return (
//       <Tooltip>
//         <TooltipTrigger asChild>
//           <Button
//             variant={"ghost"}
//             size={"icon"}
//             className="relative"
//             onPointerDown={(e) => e.stopPropagation()}
//             onClick={handleOpenRoot}
//             type="button"
//             aria-label="Open group chat"
//           >
//             <MessageSquare className="h-5 w-5 text-green-600" />
//             {unreadChatCount > 0 && (
//               <UnreadCount
//                 count={unreadChatCount}
//                 className="absolute !-top-2 !-right-2 h-5 w-5 rounded-full text-[10px]"
//               />
//             )}
//           </Button>
//         </TooltipTrigger>
//         <TooltipContent>
//           <p>Open Group Root</p>
//         </TooltipContent>
//       </Tooltip>
//     );
//   },
// );

/**
 * Sortable group item component for drag and drop
 * Only used for non-private groups
 */
function SortableGroupItemView({ group, unreadChatCount = 0, onInsertGroup }: GroupViewProps) {
  const isMobileViewOnly = useIsMobile();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id, disabled: isMobileViewOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <GroupItemViewContent
        group={group}
        unreadChatCount={unreadChatCount}
        dragHandleProps={
          isMobileViewOnly ? undefined : { ...attributes, ...listeners }
        }
        onInsertGroup={onInsertGroup}
      />
    </div>
  );
}

/**
 * Regular group item view (for private groups or non-sortable)
 */
function GroupItemView({ group, unreadChatCount = 0, onInsertGroup }: GroupViewProps) {
  return (
    <GroupItemViewContent
      group={group}
      unreadChatCount={unreadChatCount}
      onInsertGroup={onInsertGroup}
    />
  );
}

/**
 * Shared content component for group item view
 */
function GroupItemViewContent({
  group,
  unreadChatCount = 0,
  dragHandleProps,
  onInsertGroup,
}: {
  group: GroupDetails;
  unreadChatCount?: number;
  dragHandleProps?: React.HTMLAttributes<HTMLElement>;
  onInsertGroup?: (
    createdGroup: GroupDetails,
    anchorGroupId: string,
    position: GroupInsertPosition,
  ) => Promise<void> | void;
}) {
  const isMobileViewOnly = useIsMobile();
  const { setOpenMobile } = useSidebar();
  const currentActiveGroup = useItemStore((state) => state.groupId);
  const isMemberChatOpen = useChatStore((state) => state.isMemberChatOpen);
  const setMemberChatOpen = useChatStore((state) => state.setMemberChatOpen);
  const requestMemberChatComposerFocus = useChatStore(
    (state) => state.requestMemberChatComposerFocus,
  );
  const navigate = useNavigate();
  const [isRenaming, setIsRenaming] = useState(false);

  const handleStartRename = useCallback(() => {
    setIsRenaming(true);
  }, []);

  /**
   * Opens the group at its root (no last-visited folder from localStorage).
   */
  const handleGroupNavigation = useCallback(
    (e: React.MouseEvent) => {
      if (isRenaming) return;
      e.preventDefault();
      e.stopPropagation();

      if (isMobileViewOnly) {
        setOpenMobile(false);
      }
      navigate({
        to: "/group/$groupId",
        params: {
          groupId: String(group.id),
        },
      });
    },
    [group.id, isMobileViewOnly, isRenaming, navigate, setOpenMobile],
  );

  const handleOpenGroupRoot = useCallback(() => {
    setMemberChatOpen(true);
    requestMemberChatComposerFocus();
    if (isMobileViewOnly) {
      setOpenMobile(false);
    }
    navigate({
      to: "/group/$groupId",
      params: {
        groupId: String(group.id),
      },
    });
  }, [
    group.id,
    isMobileViewOnly,
    navigate,
    requestMemberChatComposerFocus,
    setMemberChatOpen,
    setOpenMobile,
  ]);

  const handleCreateAbove = useCallback(
    async (createdGroup: GroupDetails) => {
      await onInsertGroup?.(createdGroup, String(group.id), "above");
    },
    [group.id, onInsertGroup],
  );

  const handleCreateBelow = useCallback(
    async (createdGroup: GroupDetails) => {
      await onInsertGroup?.(createdGroup, String(group.id), "below");
    },
    [group.id, onInsertGroup],
  );
  const isActiveGroup = String(currentActiveGroup) === String(group.id);
  const groupChatUnreadCount =
    isActiveGroup && isMemberChatOpen ? 0 : unreadChatCount;

  return (
    <div className="group relative my-2">
      {!isMobileViewOnly && (
        <CreateNewGroupDialog
          compact
          onGroupCreated={handleCreateAbove}
          trigger={
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                "border-border bg-surface-light-sl-1 dark:bg-surface-dark-sl-1 absolute left-2 top-0 z-20 h-6 w-6 -translate-y-1/2 rounded-full border p-0 shadow-sm transition-opacity",
                "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100",
              )}
              aria-label={`Add group above ${group.name}`}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          }
        />
      )}
      <GroupSidebarContextMenu group={group} onRenameSelect={handleStartRename}>
        <div
          {...(isRenaming ? {} : (dragHandleProps ?? {}))}
          className={cn(
            "border-border text-foreground group flex h-14 grow items-center justify-between rounded-md border px-4",
            isRenaming
              ? "cursor-default"
              : dragHandleProps
                ? "cursor-grab active:cursor-grabbing"
                : "cursor-pointer",
            isActiveGroup
              ? "border-primary-p-main border-2"
              : "",
          )}
          onClick={handleGroupNavigation}
        >
          <div className="flex w-full min-w-0 items-center gap-3">
            <GroupSidebarItemName
              group={group}
              isEditing={isRenaming}
              onEditingChange={setIsRenaming}
            />
          </div>
          <div className="flex items-center gap-3">
            {group.totalUnreadCount > 0 && (
              <UnreadCount count={group.totalUnreadCount} />
            )}
            {/* <Groups.ShareGroup
              onOpenRoot={handleOpenGroupRoot}
              unreadChatCount={groupChatUnreadCount}
            /> */}
            <ChevronRight className="h-5 w-5" />
          </div>
        </div>
      </GroupSidebarContextMenu>
      {!isMobileViewOnly && (
        <CreateNewGroupDialog
          compact
          onGroupCreated={handleCreateBelow}
          trigger={
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                "border-border bg-surface-light-sl-1 dark:bg-surface-dark-sl-1 absolute left-2 bottom-0 z-20 h-6 w-6 translate-y-1/2 rounded-full border p-0 shadow-sm transition-opacity",
                "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100",
              )}
              aria-label={`Add group below ${group.name}`}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          }
        />
      )}
    </div>
  );
}
